import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClipboardList, Search, Loader2, FileText } from "lucide-react";

const AssessmentsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("assessments");
  const [assessments, setAssessments] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [totals, setTotals] = useState({ assessments: 0, intakes: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => { fetchData(); }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let aUrl = `${API}/staff/all-assessments?limit=50`;
      let iUrl = `${API}/staff/all-intake-forms?limit=50`;
      if (statusFilter !== "all") {
        aUrl += `&status=${statusFilter}`;
        iUrl += `&status=${statusFilter}`;
      }
      const [aRes, iRes] = await Promise.all([
        fetch(aUrl, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(iUrl, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (aRes.ok) { const d = await aRes.json(); setAssessments(d.assessments); setTotals(prev => ({ ...prev, assessments: d.total })); }
      if (iRes.ok) { const d = await iRes.json(); setIntakes(d.intakes); setTotals(prev => ({ ...prev, intakes: d.total })); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filterBySearch = (items, nameKey) => search
    ? items.filter(i => (i[nameKey] || i.full_name || "").toLowerCase().includes(search.toLowerCase()))
    : items;

  const statusColor = { draft: "bg-yellow-50 text-yellow-700 border-yellow-200", reviewed: "bg-blue-50 text-blue-700 border-blue-200", submitted: "bg-[#FF7A00]/10 text-[#FF7A00] border-[#FF7A00]/20", approved: "bg-green-50 text-green-700 border-green-200" };

  return (
    <div className="space-y-6" data-testid="assessments-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Assessments and Intake</h1>
        <p className="mt-1 text-base text-stone-500">Clinical assessments and pre-consultation intake forms</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 rounded-lg">
          <TabsTrigger value="assessments" className="rounded-md text-sm">Clinical Assessments ({totals.assessments})</TabsTrigger>
          <TabsTrigger value="intakes" className="rounded-md text-sm">Intake Forms ({totals.intakes})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Search by patient name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-lg" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-lg"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : tab === "assessments" ? (
        filterBySearch(assessments, "patient_name").length > 0 ? (
          <Card className="rounded-xl border-stone-200/60 overflow-hidden">
            <div className="hidden sm:grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] gap-4 px-5 py-3 bg-stone-50 text-xs font-semibold text-stone-500 uppercase tracking-wider border-b border-stone-100">
              <span>Patient</span><span>Staff</span><span>Type</span><span>Date</span><span>Status</span>
            </div>
            <div className="divide-y divide-stone-100">
              {filterBySearch(assessments, "patient_name").map(a => (
                <div key={a.assessment_id} className="px-5 py-3.5 sm:grid sm:grid-cols-[2fr_1.5fr_1fr_1fr_1fr] sm:gap-4 sm:items-center cursor-pointer hover:bg-stone-50/50" onClick={() => navigate(`/admin/patients/${a.patient_id}`)}>
                  <div>
                    <p className="text-sm font-medium text-stone-900">{a.patient_name}</p>
                    <p className="text-xs text-stone-400">{a.patient_email}</p>
                  </div>
                  <p className="text-sm text-stone-600 hidden sm:block">{a.staff_name}</p>
                  <p className="text-sm text-stone-600 capitalize hidden sm:block">{a.assessment_type}</p>
                  <p className="text-sm text-stone-500 hidden sm:block">{a.created_at?.split("T")[0]}</p>
                  <Badge variant="outline" className={`text-xs capitalize ${statusColor[a.status] || ""}`}>{a.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="rounded-xl border-stone-200/60 p-12 text-center">
            <ClipboardList className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-500">No clinical assessments found.</p>
          </Card>
        )
      ) : (
        filterBySearch(intakes, "full_name").length > 0 ? (
          <Card className="rounded-xl border-stone-200/60 overflow-hidden">
            <div className="hidden sm:grid grid-cols-[2fr_1.5fr_1fr_1fr] gap-4 px-5 py-3 bg-stone-50 text-xs font-semibold text-stone-500 uppercase tracking-wider border-b border-stone-100">
              <span>Patient</span><span>Concerns</span><span>Submitted</span><span>Status</span>
            </div>
            <div className="divide-y divide-stone-100">
              {filterBySearch(intakes, "full_name").map(i => (
                <div key={i.intake_id} className="px-5 py-3.5 sm:grid sm:grid-cols-[2fr_1.5fr_1fr_1fr] sm:gap-4 sm:items-center cursor-pointer hover:bg-stone-50/50" onClick={() => navigate(`/admin/patients/${i.patient_id}`)}>
                  <div>
                    <p className="text-sm font-medium text-stone-900">{i.full_name}</p>
                    <p className="text-xs text-stone-400">{i.email} &middot; {i.location}</p>
                  </div>
                  <p className="text-sm text-stone-600 hidden sm:block truncate">{i.main_health_concerns || "Not specified"}</p>
                  <p className="text-sm text-stone-500 hidden sm:block">{i.submitted_at?.split("T")[0]}</p>
                  <Badge variant="outline" className={`text-xs capitalize ${statusColor[i.status] || ""}`}>{i.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="rounded-xl border-stone-200/60 p-12 text-center">
            <FileText className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-500">No intake forms submitted yet.</p>
          </Card>
        )
      )}
    </div>
  );
};

export default AssessmentsPage;
